// Combobox — selector con búsqueda, mobile-friendly. Reemplaza a `<Select>`
// nativo cuando la lista es larga (ej. SKUs en captura del líder con muchas
// referencias del cliente).
//
// Decisiones de diseño:
//   - Sin dependencias externas. Radix Command sería ideal pero suma peso al
//     bundle. Esto cubre el 90% del caso.
//   - Búsqueda case-insensitive sobre el `searchText` que cada item declara
//     (típicamente codigo + descripción concatenados).
//   - Click afuera o tecla Escape → cierra. Enter selecciona el primer match.
//   - Empty state: "Sin resultados" con CTA para limpiar la búsqueda.
//   - Touch targets de 44px (HIG iOS) — apto para piso de planta con guantes.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { Input } from './input';

export interface ComboboxOption {
  value: string;
  /** Label primario mostrado en la fila (suele ser el código). */
  label: string;
  /** Texto secundario (descripción, formato, etc.) mostrado debajo. */
  description?: string;
  /** Texto sobre el que se hace match — concatenar todo lo searchable. */
  searchText: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  /** Cuando hay error de validación (border crit). */
  invalid?: boolean;
  disabled?: boolean;
  id?: string;
  /** Render custom de la fila (override del default label+description). */
  renderOption?: (option: ComboboxOption) => ReactNode;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Buscar…',
  emptyLabel = 'Sin resultados',
  invalid,
  disabled,
  id,
  renderOption,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  // Cerrar al click outside
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Cerrar con Escape
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        setQuery('');
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const filtered = filterOptions(options, query);
        const first = filtered[0];
        if (first) {
          onChange(first.value);
          setOpen(false);
          setQuery('');
        }
      }
    },
    [options, onChange, query],
  );

  const filtered = filterOptions(options, query);

  function handleSelect(opt: ComboboxOption) {
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  }

  function handleClear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange('');
    setQuery('');
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger / selected state */}
      {open ? (
        <Input
          ref={inputRef}
          id={id}
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-invalid={invalid || undefined}
          disabled={disabled}
        />
      ) : (
        <div
          className={cn(
            'flex h-11 w-full items-center justify-between rounded border border-border bg-surface-input px-3 text-left text-sm',
            'focus-within:border-accent focus-within:ring-2 focus-within:ring-accent',
            disabled && 'cursor-not-allowed opacity-50',
            invalid && 'border-crit ring-crit',
            !selected && 'text-fg-tertiary',
          )}
        >
          <button
            type="button"
            id={id}
            onClick={() => !disabled && setOpen(true)}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            className="flex min-w-0 flex-1 items-center justify-between text-left focus-visible:outline-none"
          >
            <span className="flex min-w-0 flex-col truncate text-left">
              <span className="truncate text-fg">{selected ? selected.label : placeholder}</span>
              {selected?.description ? (
                <span className="truncate text-xs text-fg-tertiary">{selected.description}</span>
              ) : null}
            </span>
            <ChevronDownIcon />
          </button>
          {selected ? (
            <button
              type="button"
              onClick={handleClear}
              disabled={disabled}
              className="ml-1 rounded p-1 text-fg-tertiary hover:text-fg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              aria-label="Limpiar selección"
            >
              ✕
            </button>
          ) : null}
        </div>
      )}

      {/* Dropdown */}
      {open ? (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 max-h-64 overflow-y-auto rounded border border-border bg-surface-elevated shadow-lg">
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-fg-tertiary">{emptyLabel}</p>
          ) : (
            <ul className="flex flex-col py-1">
              {filtered.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => handleSelect(opt)}
                    className={cn(
                      'flex min-h-[44px] w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-surface-overlay',
                      opt.value === value && 'bg-surface-overlay font-medium',
                    )}
                  >
                    {renderOption ? (
                      renderOption(opt)
                    ) : (
                      <>
                        <span className="text-fg">{opt.label}</span>
                        {opt.description ? (
                          <span className="text-xs text-fg-tertiary">{opt.description}</span>
                        ) : null}
                      </>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function filterOptions(options: ComboboxOption[], query: string): ComboboxOption[] {
  if (!query.trim()) return options;
  const q = query.toLowerCase().trim();
  return options.filter((o) => o.searchText.toLowerCase().includes(q));
}

function ChevronDownIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-fg-tertiary"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
